import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IPaymentMethod, PaymentMethod } from '../models/payment-method.model';
import { IServerResponse, ServerResponse } from '../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodService {

  public loading: boolean = false;
  public paymentMethods: PaymentMethod[] = [];

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) {
    this.getAll();
  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  // region create
  async create(
    data: { code: string; name: string }
  ): Promise<PaymentMethod | null> {
    let result: PaymentMethod | null = null;
    const urlApi = `${environment.urlApi}/payment-methods`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando método de pago...';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ paymentMethod: IPaymentMethod }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ paymentMethod: IPaymentMethod }>>(
            urlApi,
            JSON.stringify(data),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse<{ paymentMethod: IPaymentMethod }>) =>
              resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            },
          });
        }
      );

      if (response.statusCode === 201) {
        result = new PaymentMethod(response.data!.paymentMethod);
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll();
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return result;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  // region delete
  async delete(
    paymentMethod: PaymentMethod
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/payment-methods/${paymentMethod.code}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando método de pago...';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: { 'Authorization': token.toString() }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            },
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll();
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region getAll
  async getAll() {
    this.loading = true;
    const urlApi = `${environment.urlApi}/payment-methods`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ paymentMethods: IPaymentMethod[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ paymentMethods: IPaymentMethod[] }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => reject(error),
          });
        }
      );

      if (response.statusCode === 200) {
        this.paymentMethods = response.data!.paymentMethods.map(
          pm => new PaymentMethod(pm)
        );
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error de servidor',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  // region update
  async update(
    code: string,
    data: { name?: string; status?: boolean }
  ): Promise<PaymentMethod | null> {
    let result: PaymentMethod | null = null;
    const urlApi = `${environment.urlApi}/payment-methods/${code}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando método de pago...';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ paymentMethod: IPaymentMethod }>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{ paymentMethod: IPaymentMethod }>>(
            urlApi,
            JSON.stringify(data),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse<{ paymentMethod: IPaymentMethod }>) =>
              resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            },
          });
        }
      );

      if (response.statusCode === 200) {
        result = new PaymentMethod(response.data!.paymentMethod);
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll();
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return result;
  }

}
